import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  makeStyles,
} from '@material-ui/core';
import { ConfirmationDialogProps, DialogSeverity } from '../../types/ConfirmationDialog';

/**
 * 確認ダイアログのスタイル定義
 */
const useStyles = makeStyles(theme => ({
  // ベースのダイアログタイトル
  dialogTitleBase: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2, 3),
    fontWeight: 600,
  },
  // Severity別のタイトル背景色
  dialogTitleDefault: {
    backgroundColor: theme.palette.grey[50],
    color: theme.palette.text.primary,
  },
  dialogTitleInfo: {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.contrastText,
  },
  dialogTitleSuccess: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText,
  },
  dialogTitleWarning: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
  },
  dialogTitleError: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
  },
  dialogContent: {
    padding: theme.spacing(3),
  },
  dialogContentText: {
    color: theme.palette.text.primary, // メッセージテキストを明確に表示
    fontSize: '1rem',
    lineHeight: 1.5,
  },
  dialogActions: {
    padding: theme.spacing(1, 3, 2),
    gap: theme.spacing(1),
  },
  confirmButton: {
    minWidth: 80,
  },
  cancelButton: {
    minWidth: 80,
  },
}));

/**
 * 汎用確認ダイアログコンポーネント
 *
 * 各種アクションの確認を行うための再利用可能なダイアログ
 * Material-UI v4のDialogコンポーネントをベースに作成
 * severityプロパティでタイトル背景色を変更可能
 */
const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  title,
  message,
  severity = 'default',
  confirmText = 'はい',
  cancelText = 'いいえ',
  confirmColor = 'primary',
  onConfirm,
  onCancel,
}) => {
  const classes = useStyles();

  /**
   * severityに基づいてタイトルのクラス名を取得
   */
  const getTitleClassName = (severity: DialogSeverity): string => {
    const baseClass = classes.dialogTitleBase;
    const severityClasses = {
      default: classes.dialogTitleDefault,
      info: classes.dialogTitleInfo,
      success: classes.dialogTitleSuccess,
      warning: classes.dialogTitleWarning,
      error: classes.dialogTitleError,
    };

    return `${baseClass} ${severityClasses[severity]}`;
  };

  /**
   * 確認ボタン押下時の処理
   */
  const handleConfirm = () => {
    onConfirm();
  };

  /**
   * キャンセルボタン押下時の処理
   */
  const handleCancel = () => {
    onCancel();
  };

  /**
   * ダイアログの背景クリック時の処理
   * キャンセル扱いとする
   */
  const handleClose = (_event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      handleCancel();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="confirmation-dialog-title"
      aria-describedby="confirmation-dialog-description"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="confirmation-dialog-title" className={getTitleClassName(severity)}>
        {title}
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <DialogContentText
          id="confirmation-dialog-description"
          className={classes.dialogContentText}
        >
          {message}
        </DialogContentText>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={handleCancel}
          color="default"
          variant="outlined"
          className={classes.cancelButton}
        >
          {cancelText}
        </Button>
        <Button
          onClick={handleConfirm}
          color={confirmColor}
          variant="contained"
          className={classes.confirmButton}
          autoFocus
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;
